import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { StudioVault } from "@/components/studio/StudioVault";
import { TrackInspector } from "@/components/studio/TrackInspector";
import { AlbumArtManager } from "@/components/studio/AlbumArtManager";
import { LicenseTracker } from "@/components/studio/LicenseTracker";

export const TheStudio = () => {
  const [vaultTree, setVaultTree] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showInspector, setShowInspector] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vaultRes, tracksRes, licensesRes] = await Promise.all([
        axios.get(`${API}/vault/tree`),
        axios.get(`${API}/tracks`),
        axios.get(`${API}/licenses`),
      ]);
      setVaultTree(vaultRes.data);
      setTracks(tracksRes.data);
      setLicenses(licensesRes.data);
    } catch (error) {
      console.error("Failed to fetch studio data:", error);
    }
  };

  const handleSelectProject = (projectId) => {
    // Find the corresponding track based on the project name
    const project = vaultTree?.children?.find((p) => p.id === projectId);
    if (project) {
      const track = tracks.find(
        (t) => t.title.toLowerCase().replace(/\s+/g, "_") === project.name.toLowerCase().replace(/\s+/g, "_") ||
               t.title.toLowerCase() === project.name.toLowerCase()
      );
      if (track) {
        setSelectedTrack(track);
        setShowInspector(true);
      }
    }
  };

  const handleUpdateTrack = async (trackId, updates) => {
    try {
      const response = await axios.put(`${API}/tracks/${trackId}`, updates);
      setTracks((prev) =>
        prev.map((t) => (t.id === trackId ? response.data : t))
      );
      setSelectedTrack(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  };

  const handleCloseInspector = () => {
    setShowInspector(false);
    setSelectedTrack(null);
  };

  return (
    <div className="space-y-6" data-testid="the-studio-page">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="font-sans font-black text-4xl text-meta-text tracking-tight">
          The Studio
        </h1>
        <p className="text-meta-text-secondary mt-1">
          Creation & Asset Vault
        </p>
      </header>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Studio Vault - File Tree */}
        <div className="lg:col-span-1">
          <StudioVault
            tree={vaultTree}
            onSelectProject={handleSelectProject}
            selectedProjectId={selectedTrack?.id}
          />
        </div>

        {/* Track Inspector or Album Art Manager */}
        <div className="lg:col-span-2">
          {showInspector && selectedTrack ? (
            <TrackInspector
              track={selectedTrack}
              onUpdate={handleUpdateTrack}
              onClose={handleCloseInspector}
            />
          ) : (
            <AlbumArtManager tracks={tracks} onUpdateTrack={handleUpdateTrack} />
          )}
        </div>
      </div>

      {/* License Tracker */}
      <div className="mt-6">
        <LicenseTracker licenses={licenses} tracks={tracks} />
      </div>
    </div>
  );
};

export default TheStudio;
